import { getColor, ALL_METRICS } from "../../constants/constantsAnalytic";

/**
 * Transforma datos para gráficos de dos ejes (y-left / y-right)
 * funcionando tanto para casos tipo PPA/NV como TON/A.
 *
 * @param {Object} params
 * @param {Array}  params.jaulas                 Array de jaulas
 * @param {boolean} params.invert                Si true, invierte (dataset por día); si false, dataset por jaula
 * @param {Object} params.dualAxisSelection      { [keyLeft]: boolean, [keyRight]: boolean, ... }
 * @param {Array}  params.commonDays             Labels de días (cuando invert === false)
 * @param {Array}  params.alarmGraph             [leftKey, rightKey]
 * @param {Object} [params.options]
 * @param {boolean} [params.options.useDecimalsLeft=false]  Marca opcional para el dataset izquierdo
 * @param {boolean} [params.options.useDecimalsRight=false] Marca opcional para el dataset derecho
 * @param {boolean} [params.options.swapColorWhenTwo=false] Si true, intercambia colores al forzar ALL_METRICS cuando hay 2 datasets (para replicar TON/A si lo necesitas)
 * @returns {{labels: string[], datasets: any[]}}
 */
export const transformDualAxisData = (
  jaulas,
  invert,
  dualAxisSelection,
  commonDays,
  alarmGraph,
) => {
  // Seguridad básica
  const safeJaulas = Array.isArray(jaulas) ? jaulas : [];
  const safeDays   = Array.isArray(commonDays) ? commonDays : [];

  // Claves principales + backup
  const [leftKey, rightKey]   = Array.isArray(alarmGraph) ? alarmGraph : [];
  // const [leftKey2, rightKey2] = Array.isArray(alarmGraph2) ? alarmGraph2 : [];

  // const leftKey  = leftKey  ?? leftKey2;
  // const rightKey = rightKey ?? rightKey2;

  // Si no tenemos ninguna clave, no podemos construir datasets
  if (!leftKey && !rightKey) {
    return { labels: invert ? safeJaulas.map(j => j.label ?? j.name) : safeDays, datasets: [] };
  }

  // Igual que en tus funciones originales: aplanar si viene [[...]]
  const resolveSummary = (j) => {
    const vs = j?.values_summary;
    if (!vs) return [];
    return Array.isArray(vs[0]) ? vs[0] : vs;
  };

  // Modo tolerante: si no mandas toggles, muestro ambos por defecto
  const showLeft  = Boolean(dualAxisSelection?.[leftKey]  ?? true);
  const showRight = Boolean(dualAxisSelection?.[rightKey] ?? true);

  if (!invert) {
    // Un dataset por jaula (o dos si dual axis)
    const datasets = safeJaulas.flatMap((j, idx) => {
      const arr = resolveSummary(j);
      const safeArr = Array.isArray(arr) ? arr : [];

      const dsLeft = {
        label: j.label ?? j.name,
        entityKey: j.name,
        data: safeArr.map(it => it?.value?.[leftKey] ?? 0 ),
        backgroundColor: getColor(idx),
        yAxisID: 'y-left',
        useDecimals: true
      };

      const dsRight = {
        label: j.label ?? j.name,
        entityKey: j.name,
        data: safeArr.map(it => it?.value?.[rightKey]  ?? 0 ),
        backgroundColor: getColor(idx),
        yAxisID: 'y-right'
      };

      return [showLeft ? dsLeft : null, showRight ? dsRight : null].filter(Boolean);
    });

    // Si hay exactamente 2 datasets (uno por eje), fija colores deterministas
    if (datasets.length === 2) {
      datasets[0].backgroundColor =
        ALL_METRICS.find(m => m.key === leftKey)?.color || 'rgba(68,112,161,0.65)';
      datasets[1].backgroundColor =
        ALL_METRICS.find(m => m.key === rightKey)?.color || 'rgba(255,159,64,0.65)';
    }

    return { labels: safeDays, datasets };
  }

  // invert === true: un dataset por día
  const datasets = safeDays.flatMap((day, dayIndex) => {
    const valuesPerJaula = safeJaulas.map(j => {
      const arr = resolveSummary(j);
      return arr?.[dayIndex]?.value;
    });

    const dsLeft = {
      label: day,
      data: valuesPerJaula.map(v => v?.[leftKey] ?? 0),
      backgroundColor: getColor(dayIndex),
      yAxisID: 'y-left',
      useDecimals: true
    };

    const dsRight = {
      label: day,
      data: valuesPerJaula.map(v => v?.[rightKey] ?? 0),
      backgroundColor: getColor(dayIndex),
      yAxisID: 'y-right'
    };

    return [showLeft ? dsLeft : null, showRight ? dsRight : null].filter(Boolean);
  });

  return { labels: safeJaulas.map(j => j.label ?? j.name), datasets };
};
